/**
 * @providesModule TrophyDataController
 *
 */

import _ from 'lodash';
import NetClient from 'NetClient';
import Prefs from 'Preferences';
import RESTStore from 'RESTStore';
import SKZConstants from 'SKZConstants';
import SKZTrophy from 'SKZTrophy';
import Sizes from 'Sizes';

import {NativeModules, Platform} from 'react-native';

const OnError = NativeModules.SKZAPIManager.OnError;

class TrophyDataController {
  _animatedTrophies: Array;
  _animatedTrophyOnToggleCount = 5;
  initializeForTrophyLoad() {
    this._animatedTrophies = [];
    this._animatingTrophies = [];
    this._fadedInTrophies = [];
    this._fadingInTrophies = [];
    this._animatedTrophiesForToggle = [];
    this._animatedTrophyOnToggleCount = Sizes.select({
      large: 8,
      other: 7,
    });
  }

  claimTrophyForUser(userId: string, trophyId: string): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      const path = `users/${userId}/trophies/${trophyId}`;
      const params = {
        state: 'CLAIMED',
      };
      NetClient.fetchURL('PATCH', path, params, null, OnError.promptRetry)
        ?.then((payloadPatch: Object) => {
          if (payloadPatch) {
            this._updateAwardedClaimedTrophyAmounts();
            const trophy = new SKZTrophy(payloadPatch);
            resolve(trophy);
          }
        })
        .catch((errorPatch: unknown) => {
          console.debug(
            'TrophyDataController: Could not claim trophy for user with error: ' +
              JSON.stringify(errorPatch)
          );
          reject(errorPatch);
        });
    });
  }

  clearTrophiesCacheForUser(userId: string) {
    const path = `users/${userId}/trophies?sort_method=`;
    RESTStore.clearCachedResponse(path + SKZConstants.SKZ_TROPHIES_FILTER_ALL);
    RESTStore.clearCachedResponse(path + SKZConstants.SKZ_TROPHIES_FILTER_COMPLETED);
    RESTStore.clearCachedResponse(path + SKZConstants.SKZ_TROPHIES_FILTER_IN_PROGRESS);
    RESTStore.clearCachedResponse(path + SKZConstants.SKZ_TROPHIES_FILTER_ALL_V2);
  }

  // Fetches the trophy lists from user1 and user2 and calls _compareTrophyListAndMarkUserEarned with these lists.
  compareCompletedTrophies(user1: String, user2: String): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      this.fetchTrophiesForUser(user1)
        .then((userTrophies1: Array<SKZTrophy>) => {
          this.fetchTrophiesForUser(user2)
            .then((userTrophies2: Array<SKZTrophy>) => {
              resolve(this._compareTrophyListAndMarkUserEarned(userTrophies1, userTrophies2));
            })
            .catch((error: unknown) => {
              console.warn(
                'TrophyDataController : Error when fetching trophy list for current user(' +
                  user2 +
                  ')',
                error
              );
              reject(error);
            });
        })
        .catch((error: unknown) => {
          console.warn(
            'TrophyDataController : Error when fetching trophy list for other player(' +
              user1 +
              ')',
            error
          );
          reject(error);
        });
    });
  }

  fetchTrophiesForUser(
    userId: string,
    isCurrentUser = false,
    filter: string = SKZConstants.SKZ_TROPHIES_FILTER_ALL,
    shouldFailSilently = false
  ): Promise {
    return new Promise((resolve: (response: unknown) => void, reject: (error: unknown) => void) => {
      const path = `users/${userId}/trophies?sort_method=${filter}`;
      const getResponse = isCurrentUser
        ? RESTStore.getAnyResponse.bind(RESTStore)
        : RESTStore.getServerResponse.bind(RESTStore);
      const retryMethod = shouldFailSilently ? OnError.failSilently : OnError.promptRetry;
      getResponse(path, null, retryMethod, (response: Object, error: Object) => {
        if (response && !error) {
          if (response.length) {
            const trophies = SKZTrophy.make(response);
            if (filter === SKZConstants.SKZ_TROPHIES_FILTER_ALL) {
              this._updateStoredTrophyAmounts(trophies);
            }
            resolve(trophies);
          } else {
            resolve([]);
          }
        } else {
          console.debug(
            'TrophyDataController: Error in fetchTrophiesForUser: ',
            JSON.stringify(error)
          );
          reject(error);
        }
      });
    });
  }

  markTrophiesAsMounting(trophies: Array<SKZTrophy>): Array<SKZTrophy> {
    const clonedList = [];
    _.forEach(trophies, (trophy, idx) => {
      const newTrophy = _.clone(trophy);
      if (idx < this._animatedTrophyOnToggleCount) {
        // Only animate out the first few, so not to overload.
        newTrophy.shouldMount = true;
        newTrophy.shouldDismount = false;
      }
      clonedList.push(newTrophy);
    });

    return clonedList;
  }

  markTrophiesAsDismounting(trophies: Array<SKZTrophy>): Array<SKZTrophy> {
    const clonedList = [];
    // _.forEach(trophies, (trophy, idx) => {
    //   const newTrophy = _.clone(trophy);
    //   if (idx < this._animatedTrophyOnToggleCount) {
    //     // Only animate out the first few, so not to overload.
    //     newTrophy.shouldDismount = true;
    //     newTrophy.shouldMount = false;
    //   }
    //   clonedList.push(newTrophy);
    // });

    return clonedList;
  }

  hasTrophyProgressAnimated(trophy: SKZTrophy): boolean {
    return _.indexOf(this._animatedTrophies, trophy.id) > -1;
  }

  markTrophyProgressAsAnimated(trophy: SKZTrophy) {
    this._animatedTrophies.push(trophy.id);
  }

  hasTrophyProgressStartedAnimating(trophy: SKZTrophy): boolean {
    return _.indexOf(this._animatingTrophies, trophy.id) > -1;
  }

  markTrophyProgressAsAnimating(trophy: SKZTrophy) {
    this._animatingTrophies.push(trophy.id);
  }

  hasTrophyFadeInAnimated(trophy: SKZTrophy): boolean {
    return _.indexOf(this._fadedInTrophies, trophy.id) > -1;
  }

  markTrophyFadeInAsAnimated(trophy: SKZTrophy) {
    this._fadedInTrophies.push(trophy.id);
  }

  hasTrophyFadeInStartedAnimating(trophy: SKZTrophy): boolean {
    return _.indexOf(this._fadingInTrophies, trophy.id) > -1;
  }

  markTrophyFadeInAsAnimating(trophy: SKZTrophy) {
    this._fadingInTrophies.push(trophy.id);
  }

  resetToggleAnimationTracking() {
    this._animatedTrophiesForToggle = [];
  }

  hasTrophyProgressAnimatedForToggle(trophy: SKZTrophy): boolean {
    return _.indexOf(this._animatedTrophiesForToggle, trophy.id) > -1;
  }

  markTrophyProgressAsAnimatedForToggle(trophy: SKZTrophy) {
    this._animatedTrophiesForToggle.push(trophy.id);
  }

  /* Checks if a trophy in userTrophies1 exists in userTrophies2 and marks that
     trophy on userTrophies1 with userEarned as true if it does exist. If it
     doesn't exist it will mark userEarned as false and return the list of
     marked trophies. */
  _compareTrophyListAndMarkUserEarned(
    userTrophies1: Array<SKZTrophy>,
    userTrophies2: Array<SKZTrophy>
  ): Array<SKZTrophy> {
    const comparedTrophyList = [];

    if (userTrophies1 === null || userTrophies1 === undefined || userTrophies1.length === 0) {
      return [];
    }

    if (userTrophies2 === null || userTrophies2 === undefined || userTrophies2.length === 0) {
      return this._filterAndMarkUserEarnedForTrophyList(userTrophies1);
    }

    userTrophies1.forEach((trophy1: SKZTrophy) => {
      if (trophy1.isClaimed() || trophy1.isAwarded()) {
        const trophy2 = userTrophies2.find((newTrophy2: SKZTrophy): boolean => {
          if (trophy1.id === newTrophy2.id && (newTrophy2.isClaimed() || newTrophy2.isAwarded())) {
            return true;
          }
        });
        trophy1.userEarned = !!trophy2;
        comparedTrophyList.push(trophy1);
      }
    });
    return this._trophyListSortByUserEarnedValue(comparedTrophyList);
  }

  _filterAndMarkUserEarnedForTrophyList(trophyList: Array<SKZTrophy>): Array<SKZTrophy> {
    const comparedTrophyList = [];
    trophyList.forEach((trophy1: SKZTrophy) => {
      if (trophy1.isClaimed() || trophy1.isAwarded()) {
        trophy1.userEarned = false;
        comparedTrophyList.push(trophy1);
      }
    });
    return comparedTrophyList;
  }

  _trophyListSortByUserEarnedValue(trophyList: Array<SKZTrophy>): Array<SKZTrophy> {
    if (trophyList === null || trophyList === undefined || trophyList.length === 0) {
      return [];
    }

    return _.sortBy(trophyList, (trophy: SKZTrophy): boolean => {
      return !trophy.userEarned;
    });
  }

  _updateAwardedClaimedTrophyAmounts() {
    const prevAwarded = Prefs.get(SKZConstants.SKZ_TROPHIES_AWARDED);
    const prevClaimed = Prefs.get(SKZConstants.SKZ_TROPHIES_CLAIMED);
    Prefs.set({[SKZConstants.SKZ_TROPHIES_AWARDED]: prevAwarded - 1});
    Prefs.set({[SKZConstants.SKZ_TROPHIES_CLAIMED]: prevClaimed + 1});
  }

  _updateStoredTrophyAmounts(trophies: Array<SKZTrophy>) {
    const total = trophies.length;
    let awardedTotal = 0;
    let claimedTotal = 0;
    _.forEach(trophies, (trophy: SKZTrophy) => {
      if (trophy.isClaimed()) {
        claimedTotal += 1;
      } else if (trophy.isAwarded()) {
        awardedTotal += 1;
      }
    });
    Prefs.set({[SKZConstants.SKZ_TROPHIES_AWARDED]: awardedTotal});
    Prefs.set({[SKZConstants.SKZ_TROPHIES_CLAIMED]: claimedTotal});
    Prefs.set({[SKZConstants.SKZ_TROPHIES_TOTAL]: total});
  }
}

const trophyDataControllerInstance = new TrophyDataController();
export default trophyDataControllerInstance;
